import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { X, RotateCcw, ShieldOff, FileText, Megaphone, Lock, LockOpen, Save } from 'lucide-react';
import { toast } from 'sonner';
import { shutPdf } from '../lib/shutPdf';
import ShutNotice from './ShutNotice';
import { useNc, setNc, openEnds, shutArea } from '../lib/netCheck';
import { flyTo } from '../lib/vis';
import { askConfirm } from '../lib/askConfirm';

const ring = (pt, color, r, cls) => L.circleMarker(pt, { radius: r, color, weight: 3, fill: false, interactive: false, className: cls });

// map overlay: red open-end markers + shut-off area highlight
export function useNetCheckLayer(mapRef, pipes, fittings) {
  const nc = useNc();
  const gRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!gRef.current || gRef.current.__map !== map) { gRef.current = L.layerGroup().addTo(map); gRef.current.__map = map; }
    const g = gRef.current;
    g.clearLayers();
    const fx = Object.fromEntries((fittings || []).map((f) => [f.id, f]));
    const sh = nc.shut;
    if (sh) {
      sh.edges.forEach((e) => g.addLayer(L.polyline(e, { color: '#f97316', weight: 13, opacity: 0.5, interactive: false, className: 'shut-edge' })));
      sh.valves.forEach((id) => fx[id] && g.addLayer(ring([fx[id].lat, fx[id].lon], '#dc2626', 17, 'shut-valve')));
      sh.houses.forEach((id) => fx[id] && g.addLayer(ring([fx[id].lat, fx[id].lon], '#f97316', 14, 'shut-house')));
      if (fx[sh.valveId]) g.addLayer(ring([fx[sh.valveId].lat, fx[sh.valveId].lon], '#111827', 19, 'shut-origin'));
    }
    if (nc.ends) openEnds(pipes, fittings).forEach((o) => {
      const m = L.marker(o.pt, { zIndexOffset: 900, icon: L.divIcon({ className: 'oe-wrap', html: `<span class="oe-mark" data-testid="open-end-${o.pipeId.replace(/[^\w-]/g, '')}-${o.i}">!</span>`, iconSize: [24, 24], iconAnchor: [12, 12] }) });
      m.on('click', (e) => { L.DomEvent.stopPropagation(e); window.dispatchEvent(new CustomEvent('giss-open-end', { detail: { pipeId: o.pipeId, i: o.i } })); });
      g.addLayer(m);
    });
  }, [mapRef, pipes, fittings, nc]);
}

const Row = ({ label, ids, fx, t, testid }) => (
  <div className="sh-row" data-testid={testid}>
    <b>{label} ({ids.length})</b>
    {ids.length ? ids.map((id) => fx[id] && <button key={id} className="sh-chip" data-testid={`${testid}-${id}`} onClick={() => flyTo([[fx[id].lat, fx[id].lon]])}>{fx[id].name || t['fit_' + fx[id].type]}</button>) : <span className="muted"> {t.shNone}</span>}
  </div>
);

const ValveRow = ({ t, sh, fx, onToggle }) => {
  const ids = [...sh.valves, ...(sh.opened || [])];
  return (
    <div className="sh-row" data-testid="shut-valves">
      <b>{t.shClose} ({sh.valves.length})</b>
      {ids.length ? ids.map((id) => {
        const f = fx[id], open = sh.opened?.includes(id);
        return f && (
          <span key={id} className={`sh-vrow ${open ? 'is-open' : ''}`}>
            <button className="sh-chip" data-testid={`shut-valves-${id}`} onClick={() => flyTo([[f.lat, f.lon]])}>{f.name || t['fit_' + f.type]}</button>
            <button className={`sh-tog ${open ? 'open' : 'closed'}`} data-testid={`shut-toggle-${id}`} onClick={() => onToggle(id)} title={t.shSimHint}>{open ? <LockOpen size={13} /> : <Lock size={13} />} {open ? t.shOpenS : t.shClosedS}</button>
          </span>
        );
      }) : <span className="muted"> {t.shNone}</span>}
    </div>
  );
};

// result card of the shut-off analysis (live simulation: toggle valves open/closed)
export function ShutPanel({ t, project, updateProject }) {
  const nc = useNc();
  const [notice, setNotice] = useState(false);
  const sh = nc.shut;
  const calc = (opened) => setNc({ shut: shutArea(project.pipes, project.fittings, sh.valveId, opened) });
  useEffect(() => { if (sh && project) calc(sh.opened || []); }, [project?.pipes, project?.fittings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sh || !project) return null;
  const fx = Object.fromEntries((project.fittings || []).map((f) => [f.id, f]));
  const v = fx[sh.valveId];
  const toggle = (id) => calc(sh.opened?.includes(id) ? sh.opened.filter((x) => x !== id) : [...(sh.opened || []), id]);
  const apply = async () => {
    const D = document.documentElement.lang === 'de';
    if (!(await askConfirm(D ? `Achtung: Damit wird der echte Ist-Zustand geändert – ${sh.valves.length} Schieber werden als GESCHLOSSEN gespeichert (bisher nur Simulation). Wirklich übernehmen?` : `Caution: this changes the real state – ${sh.valves.length} valves will be stored as CLOSED (so far only a simulation). Really apply?`, { ok: D ? 'Ist-Zustand ändern' : 'Change real state', cancel: D ? 'Abbrechen' : 'Cancel' }))) return;
    const who = (window.prompt(D ? 'Wer ändert den Zustand? (Name für das Protokoll)' : 'Who changes the state? (name for the log)', localStorage.getItem('giss_state_who') || '') || '').trim();
    if (!who) { toast.error(D ? 'Ohne Namen wird nichts geändert' : 'Nothing changed without a name'); return; }
    localStorage.setItem('giss_state_who', who);
    const at = Date.now();
    updateProject((p) => ({ ...p, stateLog: [...(p.stateLog || []), { at, who, action: 'closed', valves: sh.valves, src: 'shut-sim' }], fittings: p.fittings.map((f) => (sh.valves.includes(f.id) ? { ...f, state: 'closed', stateBy: who, stateAt: at } : f)) }));
    toast.success(t.shApplied.replace('{n}', sh.valves.length));
  };
  return (
    <div className="sh-panel" data-testid="shut-panel">
      <div className="sh-head"><ShieldOff size={18} /> <b>{t.shTitle}</b><button className="icon-btn" data-testid="shut-close-btn" onClick={() => setNc({ shut: null })} aria-label={t.shDone}><X size={18} /></button></div>
      <p data-testid="shut-valve">{t.shValve}: <b>{v?.name || '–'}</b></p>
      <ValveRow t={t} sh={sh} fx={fx} onToggle={toggle} />
      <Row label={t.shHouses} ids={sh.houses} fx={fx} t={t} testid="shut-houses" />
      <Row label={t.shHyd} ids={sh.hydrants} fx={fx} t={t} testid="shut-hydrants" />
      <p data-testid="shut-len">{t.shLen}: <b>{sh.len.toFixed(1).replace('.', ',')} m</b>{sh.housePipes ? ` · ${sh.housePipes} HA` : ''}</p>
      {sh.open && <p className="sh-warn" data-testid="shut-open-warn">⚠️ {t.shOpen}</p>}
      <div className="row sh-actions">
        <button className="btn btn-red btn-sm" data-testid="shut-pdf-btn" onClick={() => shutPdf(t, project, sh)}><FileText size={14} /> {t.shPdf}</button>
        <button className="btn btn-accent btn-sm" data-testid="shut-notice-btn" onClick={() => setNotice(true)}><Megaphone size={14} /> {t.ntBtn}</button>
        <button className="btn btn-dark btn-sm" data-testid="shut-apply-btn" onClick={apply}><Save size={14} /> {t.shApply}</button>
        <button className="btn btn-outline btn-sm" data-testid="shut-again-btn" onClick={() => setNc({ shut: null, arm: true })}><RotateCcw size={14} /> {t.shAgain}</button>
      </div>
      {notice && <ShutNotice t={t} project={project} sh={sh} onClose={() => setNotice(false)} />}
    </div>
  );
}
